import React, { useState, useEffect } from 'react';
import {
  IcPlus,
  IcPencil,
  IcTrash,
  IcClose,
  IcTags,
  IcLayers,
  IcNetwork,
  IcPackageOpen,
} from '../../theme/icons';
import Swal from '../../theme/swal';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import productService from '../../services/productService';
import authService from '../../services/authService';
import KI from '../../theme/palette';

export default function MobileCategoryList() {
  const canManage = authService.isManagerOrAdmin();

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({ id: null, name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);

  const [subcategoryFormData, setSubcategoryFormData] = useState({
    id: null,
    name: '',
    description: '',
    category_id: null,
  });
  const [isEditingSubcategory, setIsEditingSubcategory] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchAllSubcategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAllCategories();
      const categoryData = response.results || response || [];
      setCategories(categoryData);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again later.');
      setLoading(false);
    }
  };

  const fetchAllSubcategories = async () => {
    try {
      const response = await subcategoryService.getAllSubcategories();
      const subcategoryData = response.results || response || [];
      setSubcategories(subcategoryData);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setError('Failed to load subcategories. Please try again later.');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getAllProducts();
      const productData = response || [];
      setProducts(productData);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Le nom de la catégorie est requis.');
      return;
    }

    try {
      if (isEditing) {
        await categoryService.updateCategory(formData.id, formData);
      } else {
        await categoryService.createCategory(formData);
      }

      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde de la catégorie.');
    }
  };

  const handleEdit = (category) => {
    setFormData({
      id: category.id,
      name: category.name,
      description: category.description || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({ id: null, name: '', description: '' });
    setIsEditing(false);
  };

  const handleSubcategoryChange = (e) => {
    const { name, value } = e.target;
    setSubcategoryFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAddSubcategoryClick = (categoryId) => {
    setSubcategoryFormData({
      id: null,
      name: '',
      description: '',
      category_id: categoryId,
    });
    setIsEditingSubcategory(false);
    setShowSubcategoryForm(true);
  };

  const handleEditSubcategory = (subcategory) => {
    setSubcategoryFormData({
      id: subcategory.id,
      name: subcategory.name,
      description: subcategory.description || '',
      category_id: subcategory.category_id,
    });
    setIsEditingSubcategory(true);
    setShowSubcategoryForm(true);
  };

  const handleCancelSubcategory = () => {
    setSubcategoryFormData({ id: null, name: '', description: '', category_id: null });
    setIsEditingSubcategory(false);
    setShowSubcategoryForm(false);
  };

  const handleSubmitSubcategory = async (e) => {
    e.preventDefault();

    if (!subcategoryFormData.name.trim()) {
      setError('Le nom de la sous-catégorie est requis.');
      return;
    }

    if (!subcategoryFormData.category_id) {
      setError('Veuillez sélectionner une catégorie parent.');
      return;
    }

    try {
      if (isEditingSubcategory) {
        await subcategoryService.updateSubcategory(subcategoryFormData.id, subcategoryFormData);
      } else {
        await subcategoryService.createSubcategory(subcategoryFormData);
      }

      setSubcategoryFormData({ id: null, name: '', description: '', category_id: null });
      setIsEditingSubcategory(false);
      setShowSubcategoryForm(false);
      fetchAllSubcategories();
    } catch (err) {
      console.error('Error saving subcategory:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde de la sous-catégorie.');
    }
  };

  const getProductCountForCategory = (categoryId) =>
    products.filter(
      (p) => p.category && (p.category === categoryId || p.category.id === categoryId)
    ).length;

  const getProductCountForSubcategory = (subcategoryId) =>
    products.filter(
      (p) => p.subcategory && (p.subcategory === subcategoryId || p.subcategory.id === subcategoryId)
    ).length;

  const getSubcategoryCountForCategory = (categoryId) =>
    subcategories.filter((s) => s.category_id === categoryId).length;

  const handleDeleteSubcategory = (subcategory) => {
    const productCount = getProductCountForSubcategory(subcategory.id);
    let text = `Supprimer la sous-catégorie "${subcategory.name}" ?`;
    if (productCount > 0) {
      text += ` ${productCount} produit(s) associé(s) perdront ce lien. Action irréversible.`;
    }

    Swal.fire({
      title: 'Supprimer cette sous-catégorie ?',
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.peacock,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await subcategoryService.deleteSubcategory(subcategory.id);
        fetchAllSubcategories();
        fetchProducts();
      } catch (err) {
        console.error('Error deleting subcategory:', err);
        Swal.fire('Erreur', 'Échec de la suppression de la sous-catégorie.', 'error');
      }
    });
  };

  const handleDeleteCategory = (category) => {
    const productCount = getProductCountForCategory(category.id);
    const subcategoryCount = getSubcategoryCountForCategory(category.id);

    let text = `Supprimer la catégorie "${category.name}" ?`;
    if (subcategoryCount > 0) {
      text += ` ${subcategoryCount} sous-catégorie(s) seront supprimée(s).`;
    }
    if (productCount > 0) {
      text += ` ${productCount} produit(s) perdront ce lien.`;
    }
    text += ' Action irréversible.';

    Swal.fire({
      title: 'Supprimer cette catégorie ?',
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.peacock,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await categoryService.deleteCategory(category.id);
        fetchCategories();
        fetchAllSubcategories();
        fetchProducts();
      } catch (err) {
        console.error('Error deleting category:', err);
        Swal.fire('Erreur', 'Échec de la suppression de la catégorie.', 'error');
      }
    });
  };

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien icon box + h1 + compteur retirés. */}
      <div className="mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcTags size={13} /> Gestion des catégories
        </span>
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Category Form */}
      {canManage && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-9 w-9 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center">
              {isEditing ? <IcPencil /> : <IcPlus />}
            </span>
            <h2 className="text-base font-bold text-gray-800">
              {isEditing ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-600 mb-1.5" htmlFor="name">
                Nom*
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                placeholder="Nom de la catégorie"
                required
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-sm font-medium text-gray-600 mb-1.5"
                htmlFor="description"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                placeholder="Description de la catégorie"
              ></textarea>
            </div>

            <div className="flex items-center justify-end gap-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-g px-4 py-2 rounded-xl transition-colors"
                >
                  Annuler
                </button>
              )}
              {/* Mettre à jour = primaire · Ajouter (dans une carte) = btn-add-pop. */}
              <button
                type="submit"
                className={`${isEditing ? 'btn-p' : 'btn-add-pop'} px-4 py-2 rounded-xl gap-2 transition-colors`}
              >
                {isEditing ? <IcPencil /> : <IcPlus />}
                {isEditing ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories list */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : categories.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcTags className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucune catégorie trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((category) => {
            const subs = subcategories.filter((s) => s.category_id === category.id);
            return (
              <div
                key={category.id}
                className="bg-white rounded-2xl border border-gray-200 p-4"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                    <IcLayers />
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{category.name}</div>
                    {category.description ? (
                      <div className="text-xs text-gray-500 truncate">{category.description}</div>
                    ) : null}
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {/* Compteurs = étiquettes neutres (information). */}
                      <span className="bdg bdg-neutral py-0.5 rounded-full">
                        <IcNetwork size={10} />
                        {getSubcategoryCountForCategory(category.id)} sous-cat.
                      </span>
                      <span className="bdg bdg-neutral py-0.5 rounded-full">
                        <IcPackageOpen size={10} />
                        {getProductCountForCategory(category.id)} prod.
                      </span>
                    </div>
                  </div>

                  {canManage && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(category)}
                        aria-label="Modifier la catégorie"
                        className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                      >
                        <IcPencil />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category)}
                        aria-label="Supprimer la catégorie"
                        className="h-9 w-9 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <IcTrash />
                      </button>
                    </div>
                  )}
                </div>

                {/* Subcategories stacked list */}
                {subs.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {subs.map((subcategory) => (
                      <div
                        key={`sub-${subcategory.id}`}
                        className="flex items-center gap-2 rounded-lg bg-gray-50 text-gray-700 px-3 py-2"
                      >
                        <span className="text-gray-300">└─</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{subcategory.name}</div>
                          <div className="text-[11px] text-gray-400">
                            {getProductCountForSubcategory(subcategory.id)} prod.
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleEditSubcategory(subcategory)}
                              aria-label="Modifier la sous-catégorie"
                              className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                            >
                              <IcPencil size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteSubcategory(subcategory)}
                              aria-label="Supprimer la sous-catégorie"
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <IcTrash size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {canManage && (
                  <button
                    onClick={() => handleAddSubcategoryClick(category.id)}
                    className="btn-add-block mt-3 py-2 rounded-xl gap-2 transition-colors"
                  >
                    <IcPlus size={12} />
                    Ajouter une sous-catégorie
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Subcategory modal form */}
      {showSubcategoryForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancelSubcategory();
          }}
        >
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md">
            {/* Entête de modale = aplat encre, titre blanc + ✕ (Composants §10). */}
            <div className="modal-head p-4">
              <h3 className="modal-title">
                {isEditingSubcategory ? 'Modifier la sous-catégorie' : 'Ajouter une sous-catégorie'}
              </h3>
              <button
                onClick={handleCancelSubcategory}
                aria-label="Fermer"
                className="text-gray-400 hover:text-white"
              >
                <IcClose size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitSubcategory} className="p-4">
              <div className="mb-3">
                <label
                  className="block text-sm font-medium text-gray-600 mb-1.5"
                  htmlFor="subcategory_name"
                >
                  Nom*
                </label>
                <input
                  type="text"
                  id="subcategory_name"
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryChange}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Nom de la sous-catégorie"
                  required
                />
              </div>

              <div className="mb-3">
                <label
                  className="block text-sm font-medium text-gray-600 mb-1.5"
                  htmlFor="subcategory_category"
                >
                  Catégorie parent*
                </label>
                <select
                  id="subcategory_category"
                  name="category_id"
                  value={subcategoryFormData.category_id || ''}
                  onChange={handleSubcategoryChange}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:bg-gray-50 disabled:text-gray-400"
                  required
                  disabled={isEditingSubcategory}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium text-gray-600 mb-1.5"
                  htmlFor="subcategory_description"
                >
                  Description
                </label>
                <textarea
                  id="subcategory_description"
                  name="description"
                  value={subcategoryFormData.description}
                  onChange={handleSubcategoryChange}
                  rows="2"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Description de la sous-catégorie"
                ></textarea>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelSubcategory}
                  className="btn-g px-4 py-2 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`${isEditingSubcategory ? 'btn-p' : 'btn-add-pop'} px-4 py-2 rounded-xl gap-2 transition-colors`}
                >
                  {isEditingSubcategory ? <IcPencil /> : <IcPlus />}
                  {isEditingSubcategory ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
